import { FilterState, FilterCategory, ApiFilterResponse } from '@interfaces/filter.types';
import { FILTER_CONSTANTS } from '@constants/filterConstants';

export const filterHelpers = {
  isSingleSelectCategory: (category: FilterCategory): boolean => 
    FILTER_CONSTANTS.SINGLE_SELECT_CATEGORIES.includes(category as any),

  isHierarchicalCategory: (category: FilterCategory): boolean =>
    FILTER_CONSTANTS.HIERARCHICAL_CATEGORIES.includes(category as any),

  handleFilterSelection: (
    currentValues: string[],
    selectedValue: string,
    allowMultiSelect: boolean = true
  ): string[] => {
    const isSelected = currentValues.includes(selectedValue);

    if (allowMultiSelect) {
      return isSelected
        ? currentValues.filter(v => v !== selectedValue)
        : [...currentValues, selectedValue];
    }

    return isSelected ? [] : [selectedValue];
  },

  handleHierarchicalFilterSelection: (
    currentValues: string[],
    selectedValue: string,
    filterOptions: ApiFilterResponse | null,
    category: FilterCategory
  ): string[] => {
    if (!filterOptions || !filterHelpers.isHierarchicalCategory(category)) {
      return filterHelpers.handleFilterSelection(
        currentValues,
        selectedValue,
        !filterHelpers.isSingleSelectCategory(category)
      );
    }

    if (category === 'brand_model') {
      return filterHelpers._handleBrandModelSelection(currentValues, selectedValue, filterOptions);
    }

    if (category === 'body_type') {
      return filterHelpers._handleBodyTypeSelection(currentValues, selectedValue, filterOptions);
    }

    return currentValues;
  },

  _handleBrandModelSelection: (
    currentValues: string[],
    selectedValue: string,
    filterOptions: ApiFilterResponse
  ): string[] => {
    const brands = filterOptions.brand_model || [];
    const brandParent = brands.find(b => b.brand_name === selectedValue);
    const isBrandSelected = currentValues.includes(selectedValue);

    if (brandParent?.models?.length) {
      const modelNames = brandParent.models.map(m => m.model_name);
      if (isBrandSelected) {
        return currentValues.filter(v => v !== selectedValue && !modelNames.includes(v));
      } else {
        const otherValues = currentValues.filter(v => !modelNames.includes(v) && v !== selectedValue);
        return [...otherValues, selectedValue, ...modelNames];
      }
    }

    const parentBrand = brands.find(b => b.models?.some(m => m.model_name === selectedValue));
    if (parentBrand) {
      const isModelSelected = currentValues.includes(selectedValue);
      if (isModelSelected) {
        const newValues = currentValues.filter(v => v !== selectedValue);
        const siblings = parentBrand.models.map(m => m.model_name);
        const noSiblingsSelected = !siblings.some(n => newValues.includes(n));
        return noSiblingsSelected ? newValues.filter(v => v !== parentBrand.brand_name) : newValues;
      } else {
        const newValues = [...currentValues, selectedValue];
        const siblings = parentBrand.models.map(m => m.model_name);
        const allModelsSelected = siblings.every(n => newValues.includes(n));
        return allModelsSelected ? [...newValues, parentBrand.brand_name] : newValues;
      }
    }
    
    return filterHelpers.handleFilterSelection(currentValues, selectedValue, true);
  },

  _handleBodyTypeSelection: (
    currentValues: string[],
    selectedValue: string,
    filterOptions: ApiFilterResponse
  ): string[] => {
    const bodyTypes = filterOptions.body_type || [];
    const bodyTypeParent = bodyTypes.find(bt => bt.key === selectedValue || bt.value === selectedValue);
    const isBodyTypeSelected = currentValues.includes(selectedValue);

    if (bodyTypeParent?.sub_values?.length) {
      const subValues = bodyTypeParent.sub_values.map(sub => sub.value);
      if (isBodyTypeSelected) {
        return currentValues.filter(v => v !== selectedValue && !subValues.includes(v));
      } else {
        const otherValues = currentValues.filter(v => !subValues.includes(v) && v !== selectedValue);
        return [...otherValues, selectedValue];
      }
    }

    const parentBodyType = bodyTypes.find(bt => 
      bt.sub_values?.some(sub => sub.value === selectedValue || sub.key === selectedValue)
    );
    
    if (parentBodyType) {
      const isSubValueSelected = currentValues.includes(selectedValue);
      if (isSubValueSelected) {
        const newValues = currentValues.filter(v => v !== selectedValue);
        const siblings = parentBodyType.sub_values?.map(sub => sub.value) || [];
        const noSiblingsSelected = !siblings.some(n => newValues.includes(n));
        return noSiblingsSelected ? newValues.filter(v => v !== parentBodyType.value && v !== parentBodyType.key) : newValues;
      } else {
        return [...currentValues, selectedValue];
      }
    }

    return filterHelpers.handleFilterSelection(currentValues, selectedValue, true);
  },

  getBudgetDefaults: (filterOptions: ApiFilterResponse | null) => ({
    min: filterOptions?.budget?.default_min_price || 300000,
    max: filterOptions?.budget?.default_max_price || 30000000
  }),

  getFilterMetrics: (
    state: FilterState,
    filterOptions: ApiFilterResponse | null
  ) => {
    const budgetDefaults = filterHelpers.getBudgetDefaults(filterOptions);
    
    const counts = {
      sort_by: state.sort_by.length,
      body_type: state.body_type.length,
      brand_model: state.brand_model.length,
      fuel_type: state.fuel_type.length,
      transmission: state.transmission.length,
      feature: state.feature.length,
      seat_capacity: state.seat_capacity.length,
      is_express_delivery: state.is_express_delivery ? 1 : 0,
      budget: (state.budget.min !== budgetDefaults.min || state.budget.max !== budgetDefaults.max) ? 1 : 0
    };

    const totalActiveFilters = Object.values(counts).reduce((sum, count) => sum + count, 0);

    return {
      counts,
      totalActiveFilters,
      budgetDefaults,
      hasAnyActiveFilters: totalActiveFilters > 0
    };
  },

  getFilterCount: (
    state: FilterState,
    filterKey: string,
    budgetDefaults: { min: number | string; max: number | string }
  ): number => {
    switch (filterKey) {
      case 'budget':
        return (state.budget.min !== budgetDefaults.min || state.budget.max !== budgetDefaults.max) ? 1 : 0;
      case 'is_express_delivery':
        return state.is_express_delivery ? 1 : 0;
      default:
        return (state as any)[filterKey]?.length || 0;
    }
  },

  getActiveFiltersCount: (
    state: FilterState,
    budgetDefaults: { min: number | string; max: number | string }
  ): number => {
    return filterHelpers.getFilterMetrics(state, { budget: { default_min_price: budgetDefaults.min, default_max_price: budgetDefaults.max } } as any).totalActiveFilters;
  },

  getSelectionCount: (
    state: FilterState,
    category: string,
    budgetDefaults: { min: number | string; max: number | string }
  ): number => {
    return filterHelpers.getFilterCount(state, category, budgetDefaults);
  },

  formatPrice: (price: number): string => `₹${(price / 100000).toFixed(1)}L`,

  hasActiveSelection: (
    state: FilterState,
    category: FilterCategory,
    budgetDefaults: { min: number | string; max: number | string }
  ): boolean => {
    return filterHelpers.getFilterCount(state, category, budgetDefaults) > 0;
  },

  getFilterTitleWithCount: (title: string, count: number): string =>
    count > 0 ? `${title} (${count})` : title,

  isExpressDeliveryCollection: (pathname: string): boolean =>
    pathname.includes(FILTER_CONSTANTS.EXPRESS_DELIVERY_COLLECTION_PATH),

  getFilterTitle: (filterKey: string): string =>
    FILTER_CONSTANTS.FILTER_TITLE_MAPPINGS[filterKey as FilterCategory] || 
    filterKey.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
};